<script setup lang="ts">
import { format } from "date-fns/format";
import { onMounted, ref } from "vue";

import DropdownButton from "@/components/ui/DropdownButton.vue";
import DropdownButtonItem from "@/components/ui/DropdownButtonItem.vue";
import UserAvatar from "@/components/ui/UserAvatar.vue";
import { getUserFriendlyName, type ProjectDto } from "@/dto";
import { RouteNames } from "@/router";
import { useApi } from "@/services/api";

const { tenantName, project } = defineProps<{
  tenantName: string;
  project: ProjectDto;
}>();
const emit = defineEmits(["delete"]);

const api = useApi();
const userName = ref("");

function formatDatetime(dt: string) {
  return format(new Date(dt), "d MMMM");
}

function pluralize(word: string, count: number) {
  return count > 1 ? `${word}s` : word;
}

async function fetchUser() {
  const user = await api.fetchUser(project.creator_id);
  if (user) {
    userName.value = getUserFriendlyName(user);
  }
}

onMounted(async () => {
  await fetchUser();
});
</script>

<template>
  <div class="project">
    <div class="content">
      <div class="section info label-m">
        <div class="name">{{ project.name }}</div>
        <div class="counts">
          <i class="icon icon-arrow-turn-right" />
          <div class="count experiment paragraph-s">
            <i class="icon icon-cube" />
            <em>{{ project.report_count }}</em>
            {{ pluralize("experiment", project.report_count) }}
          </div>
          <div class="count presentation paragraph-s">
            <i class="icon icon-book" />
            <em>{{ project.presentation_count }}</em>
            {{ pluralize("presentation", project.presentation_count) }}
          </div>
        </div>
      </div>
      <div class="section date paragraph-s">
        <div class="name"><i class="icon icon-calendar" /> last update</div>
        <div class="value">
          {{ formatDatetime(project.created_at) }}
        </div>
      </div>
      <div class="section paragraph-s">
        <div class="name">Created by</div>
        <div class="value">
          <UserAvatar :name="userName" />
        </div>
      </div>
      <RouterLink
        :to="{
          name: RouteNames.ProjectDetail,
          params: { tenantName: tenantName, projectName: project.name },
        }"
      />
    </div>
    <div class="actions">
      <DropdownButton icon-right="icon-more" kind="tertiary">
        <DropdownButtonItem label="Delete" icon-left="icon-trash" @click="emit('delete')" />
      </DropdownButton>
    </div>
  </div>
</template>

<style scoped>
.project {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-12);
  border: solid var(--stroke-width-m) var(--color-stroke-background-primary);
  border-radius: var(--radius-xs);
  gap: var(--spacing-6);

  .content {
    position: relative;
    display: flex;
    flex: 1;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;

    a::before {
      position: absolute;
      content: "";
      inset: 0;
    }

    .info {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-4);

      .name {
        --icon-size: 28px;

        position: relative;
        padding-left: calc(var(--icon-size) + var(--spacing-8));

        &::before {
          position: absolute;
          left: 0;
          overflow: hidden;
          width: var(--icon-size);
          height: var(--icon-size);
          border: solid var(--stroke-width-m) var(--color-stroke-background-primary);
          border-radius: var(--icon-size);
          background-image: url("@/assets/images/skore-icon.svg");
          background-position: 60% 50%;
          background-repeat: no-repeat;
          background-size: 70%;
          content: "";
        }
      }

      .counts {
        display: flex;
        flex-direction: row;
        align-items: center;
        padding-left: var(--spacing-4);
        color: var(--color-text-secondary);
        gap: var(--spacing-12);

        .count {
          display: flex;
          flex-direction: row;
          align-items: center;
          padding: 0 var(--spacing-4);
          border: solid var(--stroke-width-m) var(--color-stroke-background-primary);
          border-radius: var(--radius-xs);
          color: var(--color-text-secondary);
          gap: var(--spacing-4);

          &.experiment i {
            color: #0a6;
          }

          &.presentation i {
            color: #aa7c00;
          }

          em {
            color: var(--color-text-primary);
            font-style: normal;
          }
        }
      }
    }

    .section {
      display: flex;
      width: 30%;
      flex-direction: column;
      gap: var(--spacing-4);

      &.info {
        width: 40%;
      }

      .name {
        color: var(--color-text-secondary);

        .icon {
          vertical-align: text-bottom;
        }
      }

      .value {
        height: 24px;
      }
    }
  }
}

@media screen and (width < 890px) {
  .project {
    .content {
      .section.date {
        display: none;
      }
    }
  }
}
</style>
